import React, { useMemo, useState } from 'react';
// @ts-ignore
import styles from './SearchMedicamentRecord.module.scss';
import { useEffectAsync } from '../../../helpers';
import apiClient from '../../../Services/ApiClient';
import { TERM_ITEMS } from '../../../Modules/Term/termItems.graphql';
import moment from 'moment';
import MagicForm from '../../Form/MagicForm';
import { FormValues } from '../../../Modules/MagicForm/Interfaces/FormValues';
import { FormSelectValue } from '../../../Modules/MagicForm/Interfaces/FormSelectValue';
import {
	getMedicamentRecordPatientFormScheme,
	SearchMedicamentRecordPatientFormValues,
} from './SearchMedicamentRecordPatientsDataFormScheme';
import { TermItem } from '../../../graphql/graphql';
import PatientSelect from '../../../Modules/Patient/Components/PatientSelect/PatientSelect';
import Label from '../../Base/Label/Label';
import useTranslation from '../../../Hooks/useTranslation';
import { usePatientDataContext } from '../../../Modules/Patient/Services/PatientDataContext';

interface SearchMedicamentRecordPatientsDataProps {
	patientId?: string;
	onSuccess?: (values: FormValues) => void;
	defaultValues?: SearchMedicamentRecordPatientFormValues;
}

const SearchMedicamentRecordPatientsData: React.FC<
	SearchMedicamentRecordPatientsDataProps
> = props => {
	const { patient, getOrLoadPatient } = usePatientDataContext();
	const { onSuccess, defaultValues: changedDefaultValues } = props;
	const [personalDocumentTypes, setPersonalDocumentTypes] = useState<
		FormSelectValue[]
	>([]);
	const [initialized, setInitialized] = useState(false);
	const { t } = useTranslation();
	const [defaultValues, setDefaultValues] =
		useState<SearchMedicamentRecordPatientFormValues>({
			months: '3',
			identificationDocument: {
				documentHolder: {
					label: t('component.medicalRecordSearch.documentHolder.patient'),
					value: 'patient',
				},
			},
		});

	const [patientId, setPatientId] = useState(props.patientId);

	const formScheme = useMemo(
		() =>
			getMedicamentRecordPatientFormScheme(
				personalDocumentTypes,
				patient?.isChild || false,
			),
		[personalDocumentTypes, patient],
	);

	useEffectAsync(async () => {
		await loadPersonalDocumentTypes();
		setInitialized(true);
	}, []);

	useEffectAsync(async () => {
		if (patientId) {
			setInitialized(false);
			await loadPatientData();
			setInitialized(true);
		}
	}, [patientId]);

	const loadPersonalDocumentTypes = async () => {
		const variables = {
			termType: 'sukl_druh_dokladu',
		};

		const {
			data: { termItems },
		} = await apiClient.graphql({
			query: TERM_ITEMS,
			variables,
		});

		const tmpDocumentTypes = (termItems?.data || []).map(
			({ value, name }: TermItem) => ({
				key: value?.toString(),
				value: value?.toString(),
				title: name,
			}),
		);
		setPersonalDocumentTypes(tmpDocumentTypes);

		return tmpDocumentTypes;
	};

	const loadPatientData = async () => {
		if (changedDefaultValues && Object.keys(changedDefaultValues).length) {
			setDefaultValues(changedDefaultValues);
		} else if (patientId) {
			const tempUser = await getOrLoadPatient(patientId);
			if (tempUser) {
				setDefaultValues({
					...defaultValues,
					firstName: tempUser.profile?.firstName,
					lastName: tempUser.profile?.lastName,
					dateOfBirth: tempUser.profile?.birthDate
						? moment(tempUser.profile?.birthDate)
						: undefined,
					useAddress: !!tempUser.profile?.address,
					address: {
						street: tempUser.profile?.address?.street,
						descriptiveNumber: tempUser.profile?.address?.descriptiveNumber,
						guideNumber: tempUser.profile?.address?.guideNumber,
						city: tempUser.profile?.address?.city,
						postCode: tempUser.profile?.address?.postCode,
					},
				});
			}
		}
	};

	const handleContinue = (values: SearchMedicamentRecordPatientFormValues) => {
		onSuccess?.(values);
	};

	const handleSelectPatient = (val: any) => {
		setPatientId(val?.item?.user?.id);
	};

	if (!initialized) {
		return null;
	}

	return (
		<div className={styles.formWrapper}>
			<div className={'row'}>
				<div className={'col-lg-6 col-12'}>
					{!props.patientId && (
						<>
							<Label tx={'component.searchingPatients.searchAPatient'} />
							<PatientSelect onChange={handleSelectPatient} />
							<hr />
						</>
					)}
					<MagicForm
						formScheme={formScheme}
						defaultValues={defaultValues}
						onSubmit={handleContinue}
						submitText={'base.shared.continue'}
					/>
				</div>
			</div>
		</div>
	);
};

export default SearchMedicamentRecordPatientsData;
